import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Calendar } from "lucide-react";
import { BLOG_POSTS } from "@/data/blogPosts";

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const FeaturedCard = ({ post }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={`/blog/${post.slug}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="group relative block rounded-[28px] overflow-hidden bg-pearl shadow-[0_30px_60px_-30px_rgba(74,44,53,0.45)]"
      data-testid={`blog-featured-${post.slug}`}
    >
      <div className="grid lg:grid-cols-[1.35fr_1fr]">
        <div className="relative aspect-[16/10] lg:aspect-auto lg:min-h-[440px] overflow-hidden">
          <img
            src={post.banner}
            alt={post.bannerAlt}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.04]"
            loading="eager"
            draggable="false"
          />
          <span
            className="absolute inset-3 border border-gold/0 group-hover:border-gold/50 rounded-[20px] transition-colors duration-500 pointer-events-none"
            aria-hidden
          />
          <span
            className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-[0.32em] uppercase font-semibold text-cream"
            style={{
              background:
                "linear-gradient(135deg, #4A2C35 0%, #2B1A20 80%, #C5985A 100%)",
              border: "1px solid rgba(248,242,231,0.18)",
            }}
          >
            ✦ Latest issue
          </span>
        </div>
        <div className="p-7 lg:p-10 flex flex-col justify-center">
          <p
            className="text-[11px] lg:text-[12px] tracking-[0.32em] uppercase font-semibold mb-3"
            style={{ color: "#C5985A" }}
          >
            {post.volume}
          </p>
          <h2
            className="font-serif text-plum leading-[1.08] mb-4"
            style={{ fontSize: "clamp(28px, 3.3vw, 44px)" }}
          >
            {post.title}
          </h2>
          <p className="text-plum/70 font-serif italic text-base lg:text-lg mb-5 leading-relaxed">
            {post.dek}
          </p>
          <div className="flex items-center gap-4 text-[11px] tracking-[0.18em] uppercase text-plum/55 mb-7">
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={12} strokeWidth={2} />
              {formatDate(post.date)}
            </span>
            <span className="text-plum/25">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={12} strokeWidth={2} />
              {post.readTime}
            </span>
          </div>
          <span
            className="inline-flex items-center gap-2 self-start px-7 py-3 rounded-full text-[11px] tracking-[0.28em] uppercase font-semibold text-cream transition-all"
            style={{
              background: hovered
                ? "linear-gradient(135deg, #D4537E 0%, #B33866 100%)"
                : "linear-gradient(135deg, #4A2C35 0%, #2B1A20 100%)",
            }}
          >
            Read the editorial
            <ArrowUpRight size={14} strokeWidth={2.4} />
          </span>
        </div>
      </div>
    </Link>
  );
};

const ArchiveCard = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="group relative block rounded-[20px] overflow-hidden bg-pearl shadow-[0_18px_38px_-22px_rgba(74,44,53,0.35)] transition-shadow hover:shadow-[0_28px_56px_-26px_rgba(74,44,53,0.55)]"
    data-testid={`blog-card-${post.slug}`}
  >
    <div className="relative aspect-[16/10] overflow-hidden">
      <img
        src={post.banner}
        alt={post.bannerAlt}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.05]"
        loading="lazy"
        draggable="false"
      />
      <div
        className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out p-5 lg:p-6"
        style={{
          background:
            "linear-gradient(to top, rgba(43,26,32,0.92) 0%, rgba(43,26,32,0.78) 60%, rgba(43,26,32,0) 100%)",
        }}
      >
        <p className="text-cream text-sm lg:text-[15px] leading-relaxed line-clamp-4 font-serif">
          {post.excerpt}
        </p>
      </div>
    </div>
    <div className="p-5 lg:p-6">
      <p
        className="text-[10px] lg:text-[11px] tracking-[0.32em] uppercase font-semibold mb-2"
        style={{ color: "#C5985A" }}
      >
        {post.volume}
      </p>
      <h3
        className="font-serif text-plum leading-[1.12] mb-2 group-hover:text-blush transition-colors"
        style={{ fontSize: "clamp(20px, 1.8vw, 26px)" }}
      >
        {post.title}
      </h3>
      <p className="text-plum/65 text-sm font-serif italic leading-snug mb-4 line-clamp-2">
        {post.dek}
      </p>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5 text-[10px] tracking-[0.18em] uppercase text-plum/50">
          <span>{formatDate(post.date)}</span>
          <span className="text-plum/25">·</span>
          <span>{post.readTime}</span>
        </div>
        <span
          className="w-9 h-9 rounded-full inline-flex items-center justify-center bg-plum/5 group-hover:bg-blush text-plum group-hover:text-cream transition-all"
          aria-hidden
        >
          <ArrowUpRight size={14} strokeWidth={2.4} />
        </span>
      </div>
    </div>
  </Link>
);

const BlogIndex = () => {
  const [featured, ...archive] = BLOG_POSTS;
  return (
    <main
      className="relative min-h-screen overflow-hidden"
      style={{ background: "#FDF6F0" }}
      data-testid="blog-index"
    >
      <style>{`
        @keyframes ggBlogFade { from { opacity:0; transform: translateY(14px);} to {opacity:1; transform:translateY(0);} }
        .gg-blog-fade { animation: ggBlogFade 700ms cubic-bezier(0.22,1,0.36,1) both; }
      `}</style>
      <div className="max-w-[1280px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16 pb-20 lg:pb-28">
        <header className="text-center mb-12 lg:mb-16 gg-blog-fade">
          <p
            className="text-[11px] lg:text-[12px] tracking-[0.42em] uppercase font-semibold mb-3"
            style={{ color: "#C5985A" }}
          >
            The Style Files
          </p>
          <h1
            className="font-serif text-plum leading-[1.02]"
            style={{ fontSize: "clamp(40px, 7vw, 84px)" }}
          >
            The <span className="italic">Journal.</span>
          </h1>
          <p className="mt-5 max-w-xl mx-auto text-plum/65 font-serif italic text-base lg:text-lg">
            Editorial musings on style, jewelry, and the women who decided who
            they were — and refused to renegotiate it every morning.
          </p>
          <div className="mt-7 flex items-center justify-center gap-3">
            <span className="h-px w-12 lg:w-16 bg-gold/40" />
            <span className="text-gold text-lg" aria-hidden>✦</span>
            <span className="h-px w-12 lg:w-16 bg-gold/40" />
          </div>
        </header>
        <section className="mb-14 lg:mb-20 gg-blog-fade" style={{ animationDelay: "150ms" }}>
          <FeaturedCard post={featured} />
        </section>
        {archive.length > 0 && (
          <section className="gg-blog-fade" style={{ animationDelay: "260ms" }}>
            <div className="flex items-end justify-between mb-7 lg:mb-9 px-1">
              <div>
                <p
                  className="text-[10px] lg:text-[11px] tracking-[0.32em] uppercase font-semibold mb-1.5"
                  style={{ color: "#C5985A" }}
                >
                  Archive
                </p>
                <h2
                  className="font-serif text-plum leading-[1.05]"
                  style={{ fontSize: "clamp(24px, 3vw, 36px)" }}
                >
                  Previously, in <span className="italic">the Files.</span>
                </h2>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7">
              {archive.map((post) => (
                <ArchiveCard key={post.slug} post={post} />
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default BlogIndex;
